import { AddressFormKey } from '../../enums/addressFormKey';
import type { AddressSearchResultWithSubBuildings } from './addressQueryResponseBuilder';

export type ComparableAddress = {
  addressLine1: string;
  subBuilding: string;
  city: string;
  regionOrStateCode: string;
  postalCode: string;
};

const FIELDS_TO_COMPARE: (keyof ComparableAddress)[] = [
  'addressLine1',
  'subBuilding',
  'city',
  'regionOrStateCode',
  'postalCode',
];

/**
 * Extracts the mailing address fields from PersonalDetails form values into a
 * standardised, comparable shape.
 */
export function extractMailingAddress(
  formValues: Record<string, string | null | undefined>,
): ComparableAddress {
  return {
    addressLine1: String(formValues[AddressFormKey.ADDRESS_LINE_1] ?? '').trim(),
    subBuilding: String(formValues[AddressFormKey.SUB_BUILDING] ?? '').trim(),
    city: String(formValues[AddressFormKey.CITY] ?? '').trim(),
    regionOrStateCode: String(formValues[AddressFormKey.STATE_CODE] ?? '').trim(),
    postalCode: String(formValues[AddressFormKey.POSTAL_CODE] ?? '').trim(),
  };
}

/**
 * Extracts the merchandise (appliance location) address fields from
 * PersonalDetails form values into the same standardised, comparable shape
 * as `extractMailingAddress`. Uses explicit field names rather than deriving
 * them by string manipulation, since the merchandise FormKey values don't
 * follow a consistent `merchandiseLocation` + capitalised-field-name pattern
 * (e.g. `addressLine1` -> `merchandiseLocationLine1`, not
 * `merchandiseLocationAddressLine1`).
 */
export function extractMerchandiseAddress(
  formValues: Record<string, string | null | undefined>,
): ComparableAddress {
  return {
    addressLine1: String(formValues[AddressFormKey.MERCHANDISE_ADDRESS_LINE_1] ?? '').trim(),
    subBuilding: String(formValues[AddressFormKey.MERCHANDISE_SUB_BUILDING] ?? '').trim(),
    city: String(formValues[AddressFormKey.MERCHANDISE_CITY] ?? '').trim(),
    regionOrStateCode: String(formValues[AddressFormKey.MERCHANDISE_STATE_CODE] ?? '').trim(),
    postalCode: String(formValues[AddressFormKey.MERCHANDISE_POSTAL_CODE] ?? '').trim(),
  };
}

/**
 * Reads a field off an `AddressSearchResultWithSubBuildings` as a plain,
 * comparable string. `subBuilding` is the one field that may not be a
 * string (it's exploded into `SubBuilding[]` before the user picks a
 * specific sub-unit) - treat anything non-string as empty rather than
 * stringifying it.
 */
function toComparableString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * Compares a standardised address (built via `extractMailingAddress` or
 * `extractMerchandiseAddress`) against the address the user actually
 * selected from the search results, to determine whether the submitted
 * values still match what was picked (vs. being subsequently hand-edited).
 */
export function validateAddressMatch(
  address: ComparableAddress,
  selectedAddress: AddressSearchResultWithSubBuildings | ComparableAddress | null | undefined,
): boolean {
  if (!selectedAddress) {
    return false;
  }

  return FIELDS_TO_COMPARE.every(
    (field) => address[field] === toComparableString(selectedAddress[field]),
  );
}
